import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Briefcase } from '@phosphor-icons/react';
import { Link } from '../Link';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { CompanyFollowButton } from '../company/CompanyFollowButton';
import { adjustBrandColor } from '../../utils/brand-color/adjust-brand-color';
import { calculateFreshness } from '../dates/utils/freshnessCalculation';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../utils/routing/company-profile-url-path';
import { resolveSearchHitIsTyomarkkinatori } from '../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import {
  resolveSearchHitCompanyId,
  resolveSearchHitHostedLogoUrl,
} from '../../utils/search/resolve-search-hit-company-display';
import { HitCompanyGroupRoleRow } from './HitCompanyGroupRoleRow';
import type { DisplayJob } from '../../types';
import { useFormatMessage } from '../../i18n-review/client/Msg';

export interface HitCompanyGroupCardProps {
  /** One card's worth of this company's hits from the current page (2..MAX_ROLES_PER_GROUP_CARD). */
  items: DisplayJob[];
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
  index?: number;
}

/**
 * One card for several of the same company's open roles on this SRP page (see
 * utils/srp/group-hits-by-company.ts — same-page grouping only). Same brand-panel shell as
 * HitCompactV2, but the content column is a role list instead of one job's details. Every role
 * keeps its own full title/salary/meta, its own match-donut/save/share/more-options cluster (see
 * HitCompanyGroupRoleRow.tsx, which owns a useHitShared instance per role — hooks can't be called
 * in a loop here, so each row is its own component), and its own click-through to that job's
 * detail page. Never taller than MAX_ROLES_PER_GROUP_CARD roles by construction: when an
 * employer dominates the page (company-name queries, one big local employer),
 * group-hits-by-company.ts hands this card only one similarity-clustered slice of the company's
 * roles and renders sibling cards for the rest, so one company can't turn the SRP into a single
 * towering card. The static/SEO SRP shell renders the equivalent read-only markup via
 * HitCompanyGroupCardView.tsx. The follow button sits in the brand panel (not the header) since
 * it's a company-level action, same spot CompanyFollowButton's `onBrand` treatment is designed for.
 */
export const HitCompanyGroupCard: React.FC<HitCompanyGroupCardProps> = React.memo(({ items, sendEvent, index }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const primary = items[0];

  const adjustedBrand = useMemo(() => adjustBrandColor(primary.brandColour), [primary.brandColour]);

  const isTyomarkkinatori = useMemo(
    () => resolveSearchHitIsTyomarkkinatori(primary as unknown as Record<string, unknown>),
    [primary],
  );
  const companyLogoKey = useMemo(
    () => resolveSearchHitCompanyId(primary as unknown as Record<string, unknown>),
    [primary],
  );
  const hostedLogoUrl = useMemo(
    () => resolveSearchHitHostedLogoUrl(primary as unknown as Record<string, unknown>),
    [primary],
  );
  const companyDisplayName = primary.companyName ?? companyLogoKey;
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId: companyLogoKey, isTyomarkkinatori });
  const companyProfileHref = useMemo(
    () => (canLinkCompanyProfile && companyLogoKey ? companyProfileUrlPath(intl.locale as 'en' | 'fi' | 'sv', companyLogoKey) : ''),
    [canLinkCompanyProfile, companyLogoKey, intl.locale],
  );
  const companyLinkTitle = formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile — data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplayName },
  );

  const freshnessLabel = primary._lastSeenLabel
    ? primary._lastSeenLabel
    : (() => {
        const freshness = calculateFreshness(primary.lastSeen as string | number | undefined);
        if (!freshness) return null;
        return formatMessage(
          {
            id: freshness.id,
            defaultMessage:
              freshness.id === 'hit.lastSeen.minutesAgo'
                ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                : freshness.id === 'hit.lastSeen.hoursAgo'
                  ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                  : '{value, plural, one {{value} day ago} other {{value} days ago}}',
            description: 'Indicates how recently the job was last seen/crawled.',
          },
          { value: freshness.value },
        );
      })();

  const openRolesLabel = formatMessage(
    {
      id: 'hitGroup.openRoles',
      defaultMessage: '{count, plural, one {# open role} other {# open roles}}',
      description: 'Panel headline on a multi-role SRP card, replacing the single job\'s location — the number of open roles this company has on the current results page',
    },
    { count: items.length },
  );
  const viewProfileLabel = formatMessage({
    id: 'hitGroup.viewCompanyProfile',
    defaultMessage: 'View company profile',
    description: 'Secondary CTA at the bottom of a multi-role SRP card, linking to the full company profile (which may list more roles than shown here)',
  });

  return (
    <article className="hit-card-v2" aria-label={`${companyDisplayName} — ${openRolesLabel}`}>
      <div className="hit-card-v2__panel" style={{ background: adjustedBrand.panel, color: adjustedBrand.on }}>
        {companyProfileHref && canLinkCompanyProfile ? (
          <Link href={companyProfileHref} locale={intl.locale} className="hit-card-v2__logo-tile" title={companyLinkTitle}>
            <CompanyLogo
              companyName={companyLogoKey || ''}
              initialsSourceName={companyDisplayName ?? companyLogoKey}
              registryCompanyFormDescription={primary.registryCompanyFormDescription}
              isTyomarkkinatori={isTyomarkkinatori}
              hostedLogoUrl={hostedLogoUrl}
              size={96}
              loading={(index ?? 0) < 3 ? 'eager' : 'lazy'}
            />
          </Link>
        ) : (
          <div className="hit-card-v2__logo-tile">
            <CompanyLogo
              companyName={companyLogoKey || ''}
              initialsSourceName={companyDisplayName ?? companyLogoKey}
              registryCompanyFormDescription={primary.registryCompanyFormDescription}
              isTyomarkkinatori={isTyomarkkinatori}
              hostedLogoUrl={hostedLogoUrl}
              size={96}
              loading={(index ?? 0) < 3 ? 'eager' : 'lazy'}
            />
          </div>
        )}
        <div className="hit-card-v2__panel-location">
          <Briefcase weight="fill" className="hit-card-v2__panel-location-icon" />
          <span className="hit-card-v2__panel-location-text">{openRolesLabel}</span>
        </div>
        {companyLogoKey || freshnessLabel ? (
          <div className="hit-card-v2__panel-footer">
            {companyLogoKey ? (
              <div className="hit-card-v2__panel-follow">
                <CompanyFollowButton compact onBrand surface="company-group-card" companyId={companyLogoKey} companyName={companyDisplayName ?? companyLogoKey} />
              </div>
            ) : null}
            {freshnessLabel ? <div className="hit-card-v2__posted">{freshnessLabel}</div> : null}
          </div>
        ) : null}
      </div>

      <div className="hit-card-v2__content">
        <div className="hit-card-v2-group__header">
          {companyProfileHref && canLinkCompanyProfile ? (
            <Link href={companyProfileHref} locale={intl.locale} className="hit-card-v2-group__company-name" title={companyLinkTitle}>
              <CompanyName name={companyDisplayName ?? companyLogoKey} />
            </Link>
          ) : (
            <span className="hit-card-v2-group__company-name">
              <CompanyName name={companyDisplayName ?? companyLogoKey} />
            </span>
          )}
        </div>

        <ul className="hit-card-v2-group__role-list">
          {items.map((hit) => (
            <HitCompanyGroupRoleRow key={hit.objectID} hit={hit} sendEvent={sendEvent} panelColor={adjustedBrand.panel} />
          ))}
        </ul>

        {companyProfileHref && canLinkCompanyProfile ? (
          <Link href={companyProfileHref} locale={intl.locale} className="hit-card-v2__cta">
            {viewProfileLabel}
          </Link>
        ) : null}
      </div>
    </article>
  );
});

HitCompanyGroupCard.displayName = 'HitCompanyGroupCard';
